import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { TextField, Button, Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import { useAuth } from "../components/TokenHandler";
import { v4 as uuidv4 } from "uuid"; // Import UUID generator

const portalRoot = document.getElementById("modal-root") || document.body;

const ObservationModal = ({ isOpen, onClose, building, refreshData,id,existingKey = "" }) => {
//console.log("observation modal",id)
    const [key, setKey] = useState(existingKey); // Use passed key or leave empty
    const [corp_unique_id, setCorp_unique_id] = useState(building?.corp_unique_id || "");
    const [building_unique_id, setBuilding_unique_id] = useState(building?.building_unique_id || "");
    const [buildingName, setBuildingName] = useState(building?.legal_name || "");
    const [status, setStatus] = useState("");
    const [subject, setSubject] = useState("");
    const [notes, setNotes] = useState("");
    const [creator, setCreator] = useState(""); // Renamed from observer
    const [person_spoke_to, setPerson_spoke_to] = useState("");
    const [date, setDate] = useState(() => {
        const now = new Date();
        const localDateTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
            .toISOString()
            .slice(0, 16);
        return localDateTime;
    });
    const [method, setMethod] = useState(""); // New state for method



    const { keycloak: kc, authenticated, givenName, familyName } = useAuth();

    useEffect(() => {
        if (isOpen && building) {
            setCorp_unique_id(building?.corp_unique_id || "");
            setBuilding_unique_id(building?.building_unique_id || "");
            setBuildingName(building?.legal_name || "");
        }
    }, [isOpen, building]);

    // Set creator default value to givenName - familyName
    useEffect(() => {
        if (authenticated && givenName && familyName) {
            setCreator(`${givenName} ${familyName}`);
        }
    }, [authenticated, givenName, familyName]);

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!status || !date || !method || !notes || !creator || !person_spoke_to) {
            alert("All fields are required. Please fill out every field.");
            return; // Exit the function if any field is missing
        }

        let checkin_data;

        if (id) {
            // If ID is defined, make a PUT request
            const newKey = uuidv4(); // Generate a new UUID for the key

            checkin_data = {
                id, // Use the provided ID
                key: newKey,
                date, // Date given
                status, // Status given
                creator, // Person doing the check-in
                spoke_with: person_spoke_to,
                method, // Method of check-in
                note_content: notes,
            };
//console.log(checkin_data);
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/append-checkin`, {
                    method: "PUT",
                    headers: {
                        Authorization: `Bearer ${kc.token}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(checkin_data),
                });

                if (response.ok) {
                    const responseData = await response.json();
                   // console.log(responseData.message);

                    if (refreshData) {
                        refreshData(); // Refresh parent data
                    }

                    handleClose(); // Close modal and clear fields
                } else {
                    console.error("Failed to append check-in:", response.statusText);
                }
            } catch (error) {
                console.error("Error appending check-in:", error);
            }
        } else {
            // If ID is not defined, make a POST request
            const newKey = uuidv4();
            setKey(newKey); // Set it in state so it persists

            checkin_data = {
                original_date: date,
                latest_date: date,
                thread: [
                    {
                        key: newKey,
                        date: date,
                        method: method,
                        status: status,
                        creator: creator,
                        spoke_with: person_spoke_to,
                        note_content: notes,
                    },
                ],
                checkin_status: status,
                original_creator: creator,
                subject: subject,
            };

            const formData = {
                building_unique_id,
                corp_unique_id,
                checkin_data,
            };

            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/insert_checkin2`, {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${kc.token}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(formData),
                });



                if (response.status === 201) {
                    const responseData = await response.json();
                    //console.log(responseData.message);

                    if (refreshData) {
                        refreshData();
                    }

                    handleClose(); // Clear fields and close the modal
                } else {
                    console.error("Failed to insert check-in:", response.statusText);
                }
            } catch (error) {
                console.error("Error inserting check-in:", error);
            }
        }
    };

    const handleClose = () => {
        setKey(existingKey); // Reset to passed key or empty
        setStatus("");
        setNotes("");
        setCreator(`${givenName} ${familyName}`);
        setPerson_spoke_to("");
        setCorp_unique_id("");
        setBuilding_unique_id("");
        setBuildingName("");
        setDate("");
        setMethod("");
        setSubject("")
        onClose();
    };

    const handleStatusChange = (e) => {
        const selectedStatus = e.target.value;
        setStatus(selectedStatus);

        if (selectedStatus !== "Scheduled" && selectedStatus !== "Scheduled - Corp") {
            // If status isn't "Scheduled" or "Scheduled - Corp", default the date to the current time
            const currentTime = new Date(new Date().getTime() - new Date().getTimezoneOffset() * 60000)
                .toISOString()
                .slice(0, 16); // Format for datetime-local
            setDate(currentTime);
        }
    }


    if (!isOpen) return null;

    const modalContent = (
        <div style={styles.overlay}>
            <div style={styles.modal}>
                <h2>{buildingName}</h2>
                <form onSubmit={handleSubmit} style={styles.form}>
                    <FormControl fullWidth margin="normal" variant="outlined">
                        <InputLabel id="status-label">Status</InputLabel>
                        <Select
                            labelId="status-label"
                            value={status}
                            onChange={handleStatusChange}
                            label="Status"
                            sx={{
                                "& .MuiSelect-select": {
                                    textAlign: "left", // Aligns selected text to the left
                                },
                            }}
                        >
                            <MenuItem value="Not Scheduled">Not Scheduled</MenuItem>
                            <MenuItem value="Scheduled">Scheduled</MenuItem>
                            <MenuItem value="Customer No Show">Customer No Show</MenuItem>
                            <MenuItem value="Held and attended">Held and attended</MenuItem>
                            <MenuItem value="Held and attended - Corp">Held and attended - Corp</MenuItem>
                            <MenuItem value="Scheduled - Corp">Scheduled - Corp</MenuItem>
                        </Select>
                    </FormControl>

                    {/* Date Field */}
                    <TextField
                        label="Date"
                        type="datetime-local"
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        disabled={status !== "Scheduled"} // Disable if status isn't "Scheduled"
                    />

                    {!id && (
                        <TextField
                            label="Subject"
                            variant="outlined"
                            fullWidth
                            margin="normal"
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                        />
                    )}

                    {/* Method Dropdown */}
                    <FormControl fullWidth margin="normal" variant="outlined">
                        <InputLabel id="method-label">Method</InputLabel>
                        <Select
                            labelId="method-label"
                            value={method}
                            onChange={(e) => setMethod(e.target.value)}
                            label="Method"
                            sx={{
                                "& .MuiSelect-select": {
                                    textAlign: "left",
                                },
                            }}
                        >
                            <MenuItem value="Teams">Teams</MenuItem>
                            <MenuItem value="Onsite">Onsite</MenuItem>
                            <MenuItem value="Email">Email</MenuItem>
                            <MenuItem value="Phone">Phone</MenuItem>
                        </Select>
                    </FormControl>


                    {/* Notes Field */}
                    <TextField
                        label="Notes"
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        multiline
                        rows={4}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                    />

                    {/* Creator Field (Disabled) */}
                    <TextField
                        label="Creator"
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        value={creator}
                        disabled
                    />

                    {/* Person Spoke With */}
                    <TextField
                        label="Spoke with"
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        value={person_spoke_to}
                        onChange={(e) => setPerson_spoke_to(e.target.value)}
                    />

                    {/* Hidden Key Field */}
                    <input type="hidden" value={key} />

                    <div style={styles.buttonContainer}>
                        <Button onClick={handleClose} variant="outlined" color="secondary">
                            Cancel
                        </Button>
                        <Button type="submit" variant="contained" color="primary">
                            Submit
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );

    return ReactDOM.createPortal(modalContent, portalRoot);
};

const styles = {
    overlay: {
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1130,
    },
    modal: {
        backgroundColor: "white",
        padding: "20px",
        borderRadius: "5px",
        boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
        width: "600px",
        textAlign: "center",
        zIndex: 1131,
    },
    form: {
        display: "flex",
        flexDirection: "column",
    },
    buttonContainer: {
        marginTop: "20px",
        display: "flex",
        justifyContent: "space-between",
    },
};

export default ObservationModal;
